import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Center, Loader, Stack, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useAuth } from 'react-oidc-context';
import { useAuth as useAppAuth } from '@/hooks';
import { QUERY_KEYS } from '@/constants';
import AuthService from '@/services/auth.service';
import { impersonationService } from '@/services/impersonation.service';
import {
  getDataFromLs,
  getSuperadminHandoffParamsFromLocation,
  parseImpersonationStateFromRawStateParam,
  setDataToLs,
  stripSuperadminHandoffFromBrowserUrl,
} from '@/helpers';
import { consumeSuperadminHandoffBootstrapped } from '@/bootstrapAuthUrl';
import { AUTH_STATE, ImpersonationWarningCode, LsStoreKey } from '@/types';
import { ROUTE_ALIASES } from '@/routing/routesAliases';

const hasValidLocalAccessToken = (): boolean => {
  const accessToken = AuthService.getAccessToken();
  if (!accessToken) {
    return false;
  }

  return !AuthService.isTokenExpired();
};

const hasCompletedSuperadminHandoffSession = (): boolean => {
  if (!hasValidLocalAccessToken()) {
    return false;
  }

  return impersonationService.getSession().isImpersonating;
};

const getInitialImpersonationStateHandled = (): boolean => {
  if (consumeSuperadminHandoffBootstrapped()) {
    return true;
  }

  return hasCompletedSuperadminHandoffSession();
};

const AUTHORITY_MISMATCH_ERROR = 'authority mismatch';
const IMPERSONATION_WARNING_MESSAGES: Record<ImpersonationWarningCode, string> = {
  [ImpersonationWarningCode.MissingEmail]:
    'Impersonation state is missing X-Impersonate-User. Continuing with your regular session.',
  [ImpersonationWarningCode.InvalidEmail]:
    'Impersonation state has an invalid X-Impersonate-User email. Continuing with your regular session.',
  [ImpersonationWarningCode.InvalidReturnUrl]: 'Impersonation return URL is not trusted and was ignored.',
};

const getImpersonationStateFromCallbackUrl = (): Record<string, string> | null => {
  const searchParams = new URLSearchParams(window.location.search);
  const code = searchParams.get('code');
  if (code) {
    return null;
  }

  const { accessToken, rawState } = getSuperadminHandoffParamsFromLocation({
    search: window.location.search,
    hash: window.location.hash,
  });

  if (accessToken) {
    return null;
  }

  if (!rawState) {
    return null;
  }

  return parseImpersonationStateFromRawStateParam(rawState);
};

const hasOidcCallbackParams = (): boolean => {
  const searchParams = new URLSearchParams(window.location.search);
  return Boolean(searchParams.get('code') || searchParams.get('error'));
};

export const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const oidcAuth = useAuth();
  const { state, userInfo, bumpAuthEvaluation } = useAppAuth();
  const [isImpersonationStateHandled, setIsImpersonationStateHandled] = useState(getInitialImpersonationStateHandled);
  const isSigninRedirecting = useRef(false);
  const hasFinalizedLogoutLandingRef = useRef(false);
  const superadminHandoffRequestedRef = useRef(false);
  const hasMarkedAdminProductRedirectIntentRef = useRef(false);
  const impersonationBootstrapState = useMemo(() => getImpersonationStateFromCallbackUrl(), []);
  const isOidcCallback = useMemo(() => hasOidcCallbackParams(), []);
  const hasSuperadminHandoffPending = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('code')) {
      return false;
    }

    const { accessToken, rawState } = getSuperadminHandoffParamsFromLocation({
      search: window.location.search,
      hash: window.location.hash,
    });

    return Boolean(accessToken && rawState);
  }, []);
  const [superadminHandoffSettled, setSuperadminHandoffSettled] = useState(() => !hasSuperadminHandoffPending);
  const [isSuperadminHandoffFailed, setIsSuperadminHandoffFailed] = useState(false);

  const handleClearAndRetry = useCallback(async () => {
    await oidcAuth.clearStaleState();
    navigate(ROUTE_ALIASES.LOGIN, { replace: true });
  }, [oidcAuth, navigate]);

  const handleDismissSuperadminHandoffFailure = useCallback(async (): Promise<void> => {
    await AuthService.clearLocalAuthSession();
    impersonationService.clearSession();
    await oidcAuth.clearStaleState();
    navigate(ROUTE_ALIASES.LOGIN, { replace: true });
  }, [navigate, oidcAuth]);

  useEffect(() => {
    if (!hasSuperadminHandoffPending) {
      return;
    }

    if (AuthService.hasLogoutIntent()) {
      return;
    }

    if (superadminHandoffRequestedRef.current) {
      return;
    }

    superadminHandoffRequestedRef.current = true;

    const handleSuperadminHandoff = async (): Promise<void> => {
      const { accessToken, idToken, refreshToken, expiresIn, tokenType, scope, rawState } =
        getSuperadminHandoffParamsFromLocation({
          search: window.location.search,
          hash: window.location.hash,
        });

      if (!accessToken || !rawState) {
        setSuperadminHandoffSettled(true);
        return;
      }

      const impersonationState = parseImpersonationStateFromRawStateParam(rawState);
      if (!impersonationState) {
        setIsSuperadminHandoffFailed(true);
        setSuperadminHandoffSettled(true);
        return;
      }

      if (!AuthService.hasOidcUserInSessionStorage()) {
        // Seed the OIDC userStore with the full handoff payload so oidc-client-ts
        // can drive automaticSilentRenew via the Cognito refresh_token grant.
        // Falls back to a tokens-only LS write when the handoff lacks a refresh
        // token (the access_token still expires naturally with no renewal).
        await AuthService.applyImpersonationHandoff({
          accessToken,
          idToken,
          refreshToken,
          expiresIn,
          tokenType,
          scope,
        });
      }

      impersonationService.applyFromOidcState({ state: impersonationState });

      const churchIdFromQuery = new URLSearchParams(window.location.search).get('church_id')?.trim() || null;
      const churchIdFromHandoff = impersonationState.church_id?.trim() || null;
      const churchId = churchIdFromHandoff || churchIdFromQuery;
      if (churchId) {
        setDataToLs(LsStoreKey.LoginChurchId, churchId);
      }

      stripSuperadminHandoffFromBrowserUrl({ churchId });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CHURCH_ADMIN_CURRENT_SCOPES] });
      bumpAuthEvaluation();
      setIsImpersonationStateHandled(true);
      setSuperadminHandoffSettled(true);
    };

    void handleSuperadminHandoff();
  }, [bumpAuthEvaluation, hasSuperadminHandoffPending, queryClient]);

  useEffect(() => {
    if (!AuthService.hasLogoutIntent()) {
      return;
    }

    if (impersonationBootstrapState) {
      AuthService.clearLogoutIntent();
      return;
    }

    if (hasFinalizedLogoutLandingRef.current) {
      return;
    }

    hasFinalizedLogoutLandingRef.current = true;

    const finalizeLogoutAfterHostedUiRedirect = async (): Promise<void> => {
      await AuthService.clearLocalAuthSession();
      impersonationService.clearSession();
      await oidcAuth.clearStaleState();
      navigate(ROUTE_ALIASES.LOGIN, { replace: true });
    };

    void finalizeLogoutAfterHostedUiRedirect();
  }, [impersonationBootstrapState, navigate, oidcAuth]);

  useEffect(() => {
    if (AuthService.hasLogoutIntent()) {
      return;
    }

    if (!isOidcCallback && !impersonationBootstrapState) {
      const hasLocalSessionToken = hasValidLocalAccessToken();
      if (state === AUTH_STATE.AUTHENTICATED || hasLocalSessionToken) {
        if (!isImpersonationStateHandled && hasCompletedSuperadminHandoffSession()) {
          setIsImpersonationStateHandled(true);
        }
        return;
      }

      if (hasFinalizedLogoutLandingRef.current) {
        return;
      }

      hasFinalizedLogoutLandingRef.current = true;

      const handlePostLogoutRedirect = async (): Promise<void> => {
        await AuthService.clearLocalAuthSession();
        impersonationService.clearSession();
        navigate(ROUTE_ALIASES.LOGIN, { replace: true });
      };

      void handlePostLogoutRedirect();
      return;
    }

    if (hasSuperadminHandoffPending && !superadminHandoffSettled) {
      return;
    }

    if (hasSuperadminHandoffPending && isSuperadminHandoffFailed) {
      return;
    }

    const shouldDeferUntilOidcReady = !impersonationBootstrapState;

    if ((shouldDeferUntilOidcReady && oidcAuth.isLoading) || isImpersonationStateHandled) {
      return;
    }

    const shouldBootstrapImpersonationSignIn =
      Boolean(impersonationBootstrapState) && (state === AUTH_STATE.UNAUTHENTICATED || state === AUTH_STATE.LOADING);

    if (shouldBootstrapImpersonationSignIn && impersonationBootstrapState) {
      if (!isSigninRedirecting.current) {
        isSigninRedirecting.current = true;
        AuthService.signinRedirect({ state: impersonationBootstrapState });
        return;
      }

      return;
    }

    if (state === AUTH_STATE.UNAUTHENTICATED) {
      if (impersonationBootstrapState) {
        return;
      }

      const hasLocalSessionToken = Boolean(AuthService.getAccessToken()) && !AuthService.isTokenExpired();
      if (hasLocalSessionToken) {
        return;
      }

      impersonationService.clearSession();
      setIsImpersonationStateHandled(true);
      return;
    }

    if (state !== AUTH_STATE.AUTHENTICATED) {
      return;
    }

    if (!oidcAuth.user) {
      return;
    }

    const { warningCode } = impersonationService.applyFromOidcState({ state: oidcAuth.user.state });

    if (warningCode) {
      showNotification({
        title: 'Impersonation warning',
        message: IMPERSONATION_WARNING_MESSAGES[warningCode],
        color: 'yellow',
        autoClose: true,
      });
    }

    setIsImpersonationStateHandled(true);
  }, [
    hasSuperadminHandoffPending,
    impersonationBootstrapState,
    isImpersonationStateHandled,
    isOidcCallback,
    isSuperadminHandoffFailed,
    oidcAuth.isLoading,
    oidcAuth.user,
    state,
    superadminHandoffSettled,
  ]);

  useEffect(() => {
    if (AuthService.hasLogoutIntent()) {
      return;
    }

    if (state !== AUTH_STATE.AUTHENTICATED || !userInfo || !isImpersonationStateHandled) {
      return;
    }

    AuthService.clearLogoutIntent();
  }, [isImpersonationStateHandled, state, userInfo]);

  useEffect(() => {
    if (state !== AUTH_STATE.AUTHENTICATED || !userInfo || !isImpersonationStateHandled) {
      return;
    }

    if (hasMarkedAdminProductRedirectIntentRef.current) {
      return;
    }

    hasMarkedAdminProductRedirectIntentRef.current = true;
    AuthService.setAdminProductRedirectIntent();
  }, [isImpersonationStateHandled, state, userInfo]);

  const isAuthorityMismatch = oidcAuth.error?.message?.toLowerCase().includes(AUTHORITY_MISMATCH_ERROR);

  if (isSuperadminHandoffFailed) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Text c="red" fw={500}>
            Impersonation could not be started.
          </Text>
          <Text size="sm" c="dimmed" maw={400} ta="center">
            The sign-in link is invalid or expired. Return to the control center and try again.
          </Text>
          <Button onClick={() => void handleDismissSuperadminHandoffFailure()}>Back to sign in</Button>
        </Stack>
      </Center>
    );
  }

  if (state === AUTH_STATE.AUTHENTICATED && userInfo && isImpersonationStateHandled) {
    const pendingGroupRaw = getDataFromLs(LsStoreKey.PendingLoginGroupId);
    const pendingGroupId = typeof pendingGroupRaw === 'string' ? pendingGroupRaw.trim() : '';
    if (pendingGroupId) {
      return <Navigate to={ROUTE_ALIASES.LOGIN} replace />;
    }

    return <Navigate to={ROUTE_ALIASES.HOME} replace />;
  }

  if (hasSuperadminHandoffPending && !superadminHandoffSettled) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">
            Completing impersonation...
          </Text>
        </Stack>
      </Center>
    );
  }

  if (impersonationBootstrapState && !isOidcCallback) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">
            Redirecting to secure sign in...
          </Text>
        </Stack>
      </Center>
    );
  }

  if (state === AUTH_STATE.UNAUTHENTICATED && !oidcAuth.isLoading) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Text c="red" fw={500}>
            Sign-in failed. Please try again.
          </Text>
          {oidcAuth.error && (
            <Text size="sm" c="dimmed" maw={400} ta="center">
              {oidcAuth.error.message}
            </Text>
          )}
          {isAuthorityMismatch && (
            <Text size="sm" c="dimmed" maw={400} ta="center">
              This can happen after an auth config change. Clear the session and sign in again.
            </Text>
          )}
          {isAuthorityMismatch ? (
            <Button onClick={handleClearAndRetry}>Clear session and try again</Button>
          ) : (
            <Button component={Link} to={ROUTE_ALIASES.LOGIN}>
              Back to Sign in
            </Button>
          )}
        </Stack>
      </Center>
    );
  }

  return (
    <Center h="100vh">
      <Loader size="lg" />
    </Center>
  );
};
